/**
 * Claude Service
 * Handles Claude AI API integration
 *
 * @module ClaudeService
 * Sprint 32: OOA/OOD Refactoring
 */

import { ClaudeStatus, ClaudeChatResponse } from '../types/ii-agent-types';
import { isMainnet } from '../../config/environment';

// Claude API configuration
const CLAUDE_API_URL = 'https://api.anthropic.com/v1/messages';
const CLAUDE_MODEL = 'claude-sonnet-4-20250514';

/**
 * Interface for Claude service
 */
export interface IClaudeService {
    checkStatus(): Promise<ClaudeStatus>;
    getStatus(): ClaudeStatus | null;
    isConfigured(): boolean;
    chat(messages: Array<{role: string, content: string}>, context?: string): Promise<ClaudeChatResponse>;
    chatDirect(messages: Array<{role: string, content: string}>, apiKey: string, systemPrompt?: string): Promise<ClaudeChatResponse>;
}

/**
 * Service for Claude AI integration
 */
export class ClaudeService implements IClaudeService {
    private status: ClaudeStatus | null = null;
    private canisterClient: any = null;

    constructor() {
        // Get canister client from window
        if (typeof window !== 'undefined') {
            this.canisterClient = (window as any).canisterClient;
        }
    }

    /**
     * Check Claude API status
     */
    async checkStatus(): Promise<ClaudeStatus> {
        try {
            console.log('Checking Claude status...');

            // Check claude_proxy canister
            const isConfigured = this.canisterClient
                ? await this.canisterClient.isClaudeConfigured()
                : false;

            this.status = {
                configured: isConfigured,
                enabled: isConfigured,
                model: CLAUDE_MODEL,
                requests_today: 0
            };

            console.log('Claude status:', this.status);
            return this.status;
        } catch (error) {
            console.warn('Failed to check Claude status:', error);
            // Set default status - Claude unavailable
            this.status = {
                configured: false,
                enabled: false,
                model: 'unknown',
                requests_today: 0
            };
            return this.status;
        }
    }

    /**
     * Get current Claude status
     */
    getStatus(): ClaudeStatus | null {
        return this.status;
    }

    /**
     * Check if Claude is configured
     */
    isConfigured(): boolean {
        return this.status?.configured ?? false;
    }

    /**
     * Chat with Claude via canister (for local development)
     */
    async chat(
        messages: Array<{role: string, content: string}>,
        context?: string
    ): Promise<ClaudeChatResponse> {
        if (!this.canisterClient) {
            throw new Error('Canister client not available');
        }

        const chatRequest = {
            messages,
            context: context ? [context] : [],    // Candid opt text: [value] for Some, [] for None
            conversation_id: []                   // No conversation ID yet
        };

        const result = await this.canisterClient.chatWithClaude(chatRequest);

        if ('Ok' in result) {
            const response = result.Ok;
            return {
                message: response.message,
                conversationId: response.conversation_id,
                costEstimate: response.cost_estimate,
                inputTokens: response.input_tokens,
                outputTokens: response.output_tokens
            };
        } else {
            throw new Error(result.Err || 'Claude API request failed');
        }
    }

    /**
     * Call Claude API directly from the browser (for mainnet)
     */
    async chatDirect(
        messages: Array<{role: string, content: string}>,
        apiKey: string,
        systemPrompt?: string
    ): Promise<ClaudeChatResponse> {
        const requestBody: any = {
            model: CLAUDE_MODEL,
            max_tokens: 4096,
            messages: messages,
            temperature: 0 // Deterministic responses
        };

        if (systemPrompt) {
            requestBody.system = systemPrompt;
        }

        const response = await fetch(CLAUDE_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-api-key': apiKey,
                'anthropic-version': '2023-06-01',
                'anthropic-dangerous-direct-browser-access': 'true'
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Claude API error (${response.status}): ${errorText}`);
        }

        const data = await response.json();

        // Extract the message content
        const messageContent = data.content
            .filter((block: any) => block.type === 'text')
            .map((block: any) => block.text)
            .join('\n');

        return {
            message: messageContent,
            inputTokens: data.usage.input_tokens,
            outputTokens: data.usage.output_tokens,
            conversationId: data.id
        };
    }

    /**
     * Get API key for frontend-assisted mode (mainnet only)
     */
    async getApiKeyForFrontend(): Promise<string> {
        if (!this.canisterClient) {
            throw new Error('Canister client not available');
        }

        const result = await this.canisterClient.getApiKeyForFrontend();
        if ('Ok' in result) {
            return result.Ok;
        } else {
            throw new Error(result.Err || 'Failed to get API key');
        }
    }

    /**
     * Record response in canister for tracking (frontend-assisted mode)
     */
    async recordFrontendAssistedResponse(
        messages: Array<{role: string, content: string}>,
        claudeResponse: string,
        inputTokens: number,
        outputTokens: number,
        conversationId?: string
    ): Promise<any> {
        if (!this.canisterClient) {
            throw new Error('Canister client not available');
        }

        const trackingRequest = {
            messages: messages.map(m => ({ role: m.role, content: m.content })),
            context: [],                                      // Candid opt text: [] for None
            conversation_id: conversationId ? [conversationId] : [],  // [value] for Some, [] for None
            claude_response: claudeResponse,
            input_tokens: inputTokens,
            output_tokens: outputTokens
        };

        return this.canisterClient.chatFrontendAssisted(trackingRequest);
    }

    /**
     * Check if running on mainnet
     */
    isMainnet(): boolean {
        return isMainnet();
    }
}
